<script setup lang="ts">
import { formatCents } from '../composables/formatCents'
import { pollIntervalLabel } from '../composables/pollIntervalLabel'
import {
  productOwnerCadenceStateBadgeClass,
  productOwnerCadenceStateLabel,
} from '../composables/productOwnerCadenceState'
import type { ProductOwnerReviewAggregateRow, ProductOwnerReviewRepoRow } from '../stores/product_owner_runs'

// Per-repo breakdown for the Review tab (#1192) — one row per repo plus an
// all-repos footer, both sharing ProductOwnerReviewAggregateRow's shape. The
// trend columns (#1631) are per-repo only — the footer has no single
// interval/state to show, so it renders an em dash for those cells.
defineProps<{
  perRepo: ProductOwnerReviewRepoRow[]
  allRepos: ProductOwnerReviewAggregateRow
}>()

function formatPercentage(rate: number | null): string {
  return rate == null ? '—' : `${rate.toFixed(1)}%`
}

function formatDelta(value: number | null): string {
  if (value == null) return '—'
  return value > 0 ? `+${value}` : `${value}`
}

function formatRunsPerDay(value: number | null): string {
  return value == null ? '—' : value.toFixed(2)
}

function formatCount(value: number | null): string {
  return value == null ? '—' : `${value}`
}

// Any re-gate is the agent overriding a human, so a non-zero count is
// highlighted rather than left to blend in with the other counters.
function regatedClass(value: number | null): string {
  return value != null && value > 0 ? 'text-amber-300' : ''
}

function formatInterval(minutes: number | null): string {
  return minutes == null ? '—' : pollIntervalLabel(minutes)
}
</script>

<template>
  <div class="overflow-x-auto" data-testid="product-owner-review-repo-table">
    <table class="w-full text-sm border-collapse">
      <thead>
        <tr class="text-left text-white/40 text-xs">
          <th class="font-normal py-1 pr-3">Repo</th>
          <th class="font-normal py-1 pr-3">Runs</th>
          <th class="font-normal py-1 pr-3">Spend</th>
          <th class="font-normal py-1 pr-3">Avg / run</th>
          <th class="font-normal py-1 pr-3">Max run</th>
          <th class="font-normal py-1 pr-3">PRs merged</th>
          <th class="font-normal py-1 pr-3">$ / PR</th>
          <th class="font-normal py-1 pr-3">Idle rate</th>
          <th class="font-normal py-1 pr-3">Δ backlog 7d</th>
          <th class="font-normal py-1 pr-3">Δ backlog 30d</th>
          <th class="font-normal py-1 pr-3">Created 7d</th>
          <th class="font-normal py-1 pr-3">Closed 7d</th>
          <th class="font-normal py-1 pr-3">Created 30d</th>
          <th class="font-normal py-1 pr-3">Closed 30d</th>
          <th class="font-normal py-1 pr-3" title="Gate labels the agent put back on issues a human had just unblocked">
            Re-gated 7d
          </th>
          <th class="font-normal py-1 pr-3" title="Gate labels the agent put back on issues a human had just unblocked">
            Re-gated 30d
          </th>
          <th class="font-normal py-1 pr-3">Runs / day</th>
          <th class="font-normal py-1 pr-3">Cost / day</th>
          <th class="font-normal py-1 pr-3">Interval</th>
          <th class="font-normal py-1">State</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in perRepo"
          :key="row.repo"
          class="border-t border-white/10 text-white/70 tabular-nums"
          :data-testid="`product-owner-review-repo-row-${row.repo}`"
        >
          <td class="py-1.5 pr-3 text-white/90">{{ row.repo }}</td>
          <td class="py-1.5 pr-3">{{ row.runs }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(row.spend_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(row.avg_run_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(row.max_run_cents) }}</td>
          <td class="py-1.5 pr-3">{{ row.merged_pr_count }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(row.cost_per_merged_pr_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatPercentage(row.idle_rate) }}</td>
          <td class="py-1.5 pr-3">{{ formatDelta(row.backlog_change_7d) }}</td>
          <td class="py-1.5 pr-3">{{ formatDelta(row.backlog_change_30d) }}</td>
          <td class="py-1.5 pr-3">{{ formatCount(row.issues_created_7d) }}</td>
          <td class="py-1.5 pr-3">{{ formatCount(row.issues_closed_7d) }}</td>
          <td class="py-1.5 pr-3">{{ formatCount(row.issues_created_30d) }}</td>
          <td class="py-1.5 pr-3">{{ formatCount(row.issues_closed_30d) }}</td>
          <td class="py-1.5 pr-3" :class="regatedClass(row.regated_issues_7d)">
            {{ formatCount(row.regated_issues_7d) }}
          </td>
          <td class="py-1.5 pr-3" :class="regatedClass(row.regated_issues_30d)">
            {{ formatCount(row.regated_issues_30d) }}
          </td>
          <td class="py-1.5 pr-3">{{ formatRunsPerDay(row.runs_per_day) }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(row.cost_per_day_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatInterval(row.current_interval_minutes) }}</td>
          <td class="py-1.5">
            <span
              class="px-1.5 py-0.5 rounded text-xs font-normal"
              :class="productOwnerCadenceStateBadgeClass(row.cadence_state)"
              >{{ productOwnerCadenceStateLabel(row.cadence_state) }}</span
            >
          </td>
        </tr>
        <tr
          class="border-t border-white/20 text-white font-medium tabular-nums"
          data-testid="product-owner-review-repo-row-all"
        >
          <td class="py-1.5 pr-3">All repos</td>
          <td class="py-1.5 pr-3">{{ allRepos.runs }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(allRepos.spend_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(allRepos.avg_run_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(allRepos.max_run_cents) }}</td>
          <td class="py-1.5 pr-3">{{ allRepos.merged_pr_count }}</td>
          <td class="py-1.5 pr-3">{{ formatCents(allRepos.cost_per_merged_pr_cents) }}</td>
          <td class="py-1.5 pr-3">{{ formatPercentage(allRepos.idle_rate) }}</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5 pr-3">—</td>
          <td class="py-1.5">—</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
